import { useRef } from 'react';
import { useAppel } from '../../appels/useAppel';
import { usePiegeFocus } from '../../lib/usePiegeFocus';

/**
 * Appel entrant, visible par-dessus tout écran de l'application (pas
 * seulement `/messages`) — comme sur Messenger ou Instagram. Pas de fermeture
 * par clic à l'extérieur ni bouton « Fermer » : un appel se refuse ou
 * s'accepte, il ne se ferme pas par mégarde.
 */
export function SonnerieEntrante(): JSX.Element | null {
  const { etat, accepter, refuser } = useAppel();

  // Au clavier : focus sur la première commande, `Échap` refuse l'appel.
  const fenetre = useRef<HTMLDivElement>(null);
  usePiegeFocus(fenetre, etat.phase === 'appel-entrant', refuser, { focusInitial: 'premier' });

  if (etat.phase !== 'appel-entrant') return null;

  const { appelant, typeAppel } = etat;

  return (
    <div
      ref={fenetre}
      role="dialog"
      aria-modal="true"
      aria-label={`Appel ${typeAppel === 'video' ? 'visio' : 'audio'} entrant de ${appelant.prenom} ${appelant.nom}`}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-codialis-navy/60 p-4"
    >
      <div className="w-full max-w-sm rounded-3xl bg-white p-4 sm:p-8 text-center shadow-card">
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-codialis-navy font-display text-3xl font-bold text-white">
          {appelant.initiales}
        </div>
        <div className="mt-4 font-display text-xl font-bold text-codialis-navy">
          {appelant.prenom} {appelant.nom}
        </div>
        <div className="mt-1 flex items-center justify-center gap-1.5 text-sm text-codialis-gray">
          <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            {typeAppel === 'video' ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            ) : (
              <path
                strokeLinecap="round"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            )}
          </svg>
          Appel {typeAppel === 'video' ? 'visio' : 'audio'} entrant…
        </div>

        <div className="mt-8 flex items-center justify-center gap-8">
          <button
            type="button"
            onClick={refuser}
            aria-label="Refuser l'appel"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition hover:bg-red-700 active:scale-95"
          >
            <svg aria-hidden="true" className="h-7 w-7 rotate-[135deg]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={accepter}
            aria-label="Répondre à l'appel"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-codialis-mint text-codialis-navy shadow-lg transition hover:opacity-90 active:scale-95"
          >
            <svg aria-hidden="true" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
